import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarClock, Play, Save, Loader2, Zap, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso || "—"; }
};
const LR_META = {
  completed: { l: "Tamamlandı", c: "bg-emerald-100 text-emerald-700" },
  deferred: { l: "Ertelendi (süre doldu)", c: "bg-amber-100 text-amber-700" },
  running: { l: "Çalışıyor", c: "bg-sky-100 text-sky-700" },
};
const barColor = (h) => h.dry ? "#94a3b8" : h.status === "completed" ? "#10b981" : h.status === "deferred" ? "#f59e0b" : h.status === "running" ? "#0ea5e9" : "#cbd5e1";

export default function MaintenanceWindowCard() {
  const [st, setSt] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmReal, setConfirmReal] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/maintenance-window/status").then((r) => { setSt(r.data); setCfg(r.data.config); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/maintenance-window/config", cfg); setCfg(data); toast.success("Bakım penceresi ayarları kaydedildi"); load(); }
    catch { toast.error("Kaydedilemedi"); } finally { setBusy(false); }
  };
  const runDry = async () => {
    setBusy(true);
    try { await api.post("/admin/maintenance-window/run?dry=true"); toast.success("Kuru test başlatıldı — birazdan durum güncellenecek (ağır iş yapılmaz)"); setTimeout(load, 2500); }
    catch { toast.error("Başlatılamadı"); } finally { setBusy(false); }
  };
  const runReal = async () => {
    setBusy(true); setConfirmReal(false);
    try { await api.post("/admin/maintenance-window/run?dry=false"); toast.success("Gerçek bakım başlatıldı — sistem bakım moduna alındı, iş bitince otomatik açılacak"); setTimeout(load, 3000); }
    catch { toast.error("Başlatılamadı"); } finally { setBusy(false); }
  };

  if (!st || !cfg) return null;
  const p = st.progress || {};
  const lr = st.last_run;
  const lrm = lr ? (lr.skipped ? { l: "Atlandı", c: "bg-slate-100 text-slate-500" } : (LR_META[lr.status] || { l: lr.status || "—", c: "bg-slate-100 text-slate-600" })) : null;
  const chart = [...(st.history || [])].reverse().map((h, i) => ({ ...h, label: fmt(h.at).split(" ")[0] || `#${i}` }));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="maint-window-card">
      <p className="flex items-center gap-2 font-heading text-base font-bold text-slate-900"><CalendarClock className="h-4 w-4 text-brand-green" /> Otomatik Bakım Penceresi (Salı)</p>
      <p className="mt-1 text-xs text-slate-500">{st.schedule}. Aktif kullanıcı olsa da çalışır; işlemler en geç bitiş saatinde durur ve sistem otomatik açılır. Kalan iş sonraki haftaya ertelenir.</p>

      <div className="mt-4 flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5">
          <Switch data-testid="mw-enabled" checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
          <Label className="mb-0 text-sm font-semibold">{cfg.enabled ? "Etkin" : "Devre dışı"}</Label>
        </div>
        <div>
          <Label className="text-xs">Bitiş saati</Label>
          <div className="mt-1 flex items-center gap-1">
            <Input data-testid="mw-end-hour" type="number" min="0" max="23" value={cfg.end_hour} onChange={(e) => setCfg({ ...cfg, end_hour: Number(e.target.value) })} className="w-16" />
            <span className="text-slate-400">:</span>
            <Input data-testid="mw-end-min" type="number" min="0" max="59" value={cfg.end_min} onChange={(e) => setCfg({ ...cfg, end_min: Number(e.target.value) })} className="w-16" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Parti boyutu</Label>
          <Input data-testid="mw-batch" type="number" min="1" max="1000" value={cfg.batch_limit} onChange={(e) => setCfg({ ...cfg, batch_limit: Number(e.target.value) })} className="mt-1 w-24" />
        </div>
        <div>
          <Label className="text-xs">Yöntem</Label>
          <div className="mt-1 flex gap-1">
            {[["scraper", "Ücretsiz"], ["ai", "Google AI"]].map(([m, l]) => (
              <button key={m} data-testid={`mw-method-${m}`} onClick={() => setCfg({ ...cfg, method: m })}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${cfg.method === m ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
            ))}
          </div>
        </div>
        <Button data-testid="mw-save" onClick={save} disabled={busy} className="bg-brand-green text-white hover:bg-brand-green-600">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
        </Button>
        <Button data-testid="mw-run-dry" onClick={runDry} disabled={busy} variant="outline">
          <Play className="mr-1.5 h-4 w-4" /> Şimdi çalıştır (kuru test)
        </Button>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Piyasa öğrenme ilerlemesi (haftalar boyunca)</span>
          <span data-testid="mw-progress-pct" className="font-semibold text-slate-700">%{p.pct ?? 0} · {p.processed_so_far ?? 0}/{p.total ?? st.products_total ?? 0} ürün</span>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-brand-green transition-all" style={{ width: `${Math.min(100, p.pct ?? 0)}%` }} />
        </div>
      </div>

      {lr && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid="mw-last-run">
          <span className={`rounded-full px-2 py-0.5 font-bold ${lrm.c}`}>{lrm.l}</span>
          <span className="text-slate-400">Son çalışma: {fmt(lr.started_at || lr.at)}{lr.dry ? " · kuru test" : ""}{lr.processed != null ? ` · ${lr.processed} ürün işlendi` : ""}</span>
        </div>
      )}

      {/* Bakım geçmişi mini grafik */}
      {chart.length > 0 && (
        <div className="mt-4 rounded-xl bg-slate-50 p-3" data-testid="mw-history">
          <p className="mb-2 text-xs font-semibold text-slate-600">Bakım Geçmişi (son {chart.length} çalışma · işlenen ürün)</p>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={chart} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={0} />
              <Tooltip formatter={(v, _n, item) => [`${v} ürün${item?.payload?.dry ? " (kuru test)" : ""}`, item?.payload?.status || ""]} labelFormatter={(l) => l} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="processed" radius={[4, 4, 0, 0]}>
                {chart.map((h, i) => <Cell key={i} fill={barColor(h)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-1 flex flex-wrap gap-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-emerald-500" /> Tamamlandı</span>
            <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-amber-500" /> Ertelendi</span>
            <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-slate-400" /> Kuru test</span>
          </div>
        </div>
      )}

      {/* Manuel gerçek tetikleme (acil bakım) */}
      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="mw-real-run">
        {!confirmReal ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-amber-800"><b>Acil bakım:</b> Salı'yı beklemeden bakımı şimdi gerçekten başlatır (sistem bakım moduna alınır, iş bitince otomatik açılır).</p>
            <Button data-testid="mw-run-real" onClick={() => setConfirmReal(true)} disabled={busy} variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100">
              <Zap className="mr-1.5 h-4 w-4" /> Bakımı Şimdi Başlat (gerçek)
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Emin misiniz? Tüm kullanıcılar bakım moduna alınacak.</p>
            <div className="flex gap-2">
              <Button data-testid="mw-run-real-confirm" onClick={runReal} disabled={busy} className="bg-amber-600 text-white hover:bg-amber-700">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Zap className="mr-1.5 h-4 w-4" />} Evet, başlat
              </Button>
              <Button data-testid="mw-run-real-cancel" onClick={() => setConfirmReal(false)} variant="ghost">Vazgeç</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
